import { normalizeText } from '@quadralize/utils'
import { useCallback, useState } from 'react'
import { useDebounce } from './use-debounce'

/**
 * Props for the useCrudList hook.
 * @template TData The type of the items in the list.
 * @template TFilters The type of the filters object.
 */
interface UseCrudListProps<TFilters extends Record<string, unknown>> {
  /** Initial filter values. Default is empty object. */
  defaultFilters?: TFilters
  /** Delay in MS for the debounced search. Default is 300ms. */
  debounceDelay?: number
}

/**
 * A standard hook to manage common logic for CRUD listing screens.
 *
 * It centralizes:
 * - **Search**: Local search state and debounced search value for API calls.
 * - **Filters**: Generic filter state management (e.g., isActive, category).
 * - **Modals**: State and handlers for Create, Edit, and Status change dialogs,
 *   plus extensible support for **custom dialogs**.
 *
 * @template TData The type of the items in the list.
 * @template TFilters The type of the filters object.
 * @template TCustomDialogs Keys for any additional dialogs (e.g., 'delete' | 'details').
 */
export function useCrudList<
  TData,
  TFilters extends Record<string, unknown> = Record<string, unknown>,
  TCustomDialogs extends string = string,
>({ defaultFilters, debounceDelay = 300 }: UseCrudListProps<TFilters> = {}) {
  // Search state
  const [search, setSearch] = useState('')
  const rawDebouncedSearch = useDebounce(search, debounceDelay)
  const debouncedSearch = normalizeText(rawDebouncedSearch)

  // Filters state
  const [filters, setFilters] = useState<TFilters>((defaultFilters || {}) as TFilters)

  // Standard Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<TData | null>(null)
  const [statusItem, setStatusItem] = useState<TData | null>(null)

  // Custom Dialog states
  const [customDialogs, setCustomDialogs] = useState<Record<TCustomDialogs, TData | null>>(
    {} as Record<TCustomDialogs, TData | null>,
  )

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
  }, [])

  const setFilter = useCallback(<K extends keyof TFilters>(key: K, value: TFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Standard handlers
  const handleEdit = useCallback((item: TData) => {
    setEditingItem(item)
    setFormDialogOpen(true)
  }, [])

  const handleToggleStatus = useCallback((item: TData) => {
    setStatusItem(item)
  }, [])

  const handleCreateNew = useCallback(() => {
    setEditingItem(null)
    setFormDialogOpen(true)
  }, [])

  const handleFormClose = useCallback(() => {
    setFormDialogOpen(false)
    setEditingItem(null)
  }, [])

  const handleStatusClose = useCallback(() => {
    setStatusItem(null)
  }, [])

  // Custom dialog handlers
  const openCustomDialog = useCallback((key: TCustomDialogs, item: TData) => {
    setCustomDialogs((prev) => ({ ...prev, [key]: item }))
  }, [])

  const closeCustomDialog = useCallback((key: TCustomDialogs) => {
    setCustomDialogs((prev) => ({ ...prev, [key]: null }))
  }, [])

  return {
    // Search
    search,
    debouncedSearch,
    handleSearchChange,

    // Filters
    filters,
    setFilter,
    setFilters,

    // Dialog states/handlers
    formDialogOpen,
    editingItem,
    statusItem,
    handleEdit,
    handleToggleStatus,
    handleCreateNew,
    handleFormClose,
    handleStatusClose,

    // Custom dialogs
    customDialogs,
    openCustomDialog,
    closeCustomDialog,
  }
}
